const ALPHA_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
const ALPHA_ALIASES: Record<string, string> = { XXL: '2XL', XXXL: '3XL' };
const NUMERIC_SIZE = /^\d+(\.\d+)?$/;
const AGE_RANGE = /^(\d+)-(\d+)M$/;
const COMBINED_SIZE = /^([A-Z0-9]+)[/-]([A-Z0-9]+)$/;

type SizeRank = { group: number; value: number | string };

const alphaIndex = (size: string) => ALPHA_SIZES.indexOf(ALPHA_ALIASES[size] ?? size);

const rankSize = (size: string): SizeRank => {
  const normalized = size.trim().toUpperCase();

  if (normalized === 'OS') return { group: 0, value: 0 };
  if (NUMERIC_SIZE.test(normalized)) return { group: 1, value: Number(normalized) };

  const age = normalized.match(AGE_RANGE);
  if (age) return { group: 2, value: Number(age[1]) };

  const alpha = alphaIndex(normalized);
  if (alpha !== -1) return { group: 3, value: alpha };

  // S/M, L-XL: right after the first half.
  const combined = normalized.match(COMBINED_SIZE);
  if (combined && alphaIndex(combined[1]) !== -1 && alphaIndex(combined[2]) !== -1) {
    return { group: 3, value: alphaIndex(combined[1]) + 0.5 };
  }

  return { group: 4, value: normalized };
};

const compareCanonical = (a: string, b: string): number => {
  const rankA = rankSize(a);
  const rankB = rankSize(b);

  if (rankA.group !== rankB.group) return rankA.group - rankB.group;
  if (typeof rankA.value === 'number' && typeof rankB.value === 'number') return rankA.value - rankB.value;
  return String(rankA.value).localeCompare(String(rankB.value));
};

/** Listed sizes keep their index; unlisted ones slot in before the first listed size that sorts after them. */
const rankInOrder = (size: string, order: string[]): number => {
  const index = order.indexOf(size);
  if (index !== -1) return index;

  const next = order.findIndex((listed) => compareCanonical(size, listed) < 0);
  return (next === -1 ? order.length : next) - 0.5;
};

/**
 * Canonical order: OS, numeric ascending, age ranges (3-6M ...), XXS → 6XL (S/M after S), then unknown values
 * alphabetically. `order` (per-brand CT config) overrides it for the sizes it lists. Missing sizes go last.
 */
export const compareSizes = (a: string | undefined, b: string | undefined, order?: string[]): number => {
  if (a === b) return 0;
  if (a === undefined || a === null) return 1;
  if (b === undefined || b === null) return -1;

  if (order?.length) {
    const byOrder = rankInOrder(a, order) - rankInOrder(b, order);
    if (byOrder !== 0) return byOrder;
  }

  return compareCanonical(a, b);
};
